/**
 * persistence/managers/SyncManager.js
 *
 * Orchestrates all outbound data synchronisation:
 *   1. REST API sync  — pushes the match string to push_match_video_string/v2/
 *   2. Firestore sync — queues checkpoint + backup writes to Firestore
 *
 * The SyncManager is the ONLY component that writes to the REST API.
 * InputBoard's 40-second setInterval and direct axios calls are replaced by
 * this manager.
 *
 * Trigger sources (all call scheduleApiSync):
 *   - Typing pause  (5 s debounce after last SET_FULL_ANALYSE_STRING)
 *   - Periodic      (2-min interval, regardless of typing activity)
 *   - Visibility    (window becomes hidden / document becomes visibilityState hidden)
 *   - Beforeunload  (page is about to close)
 *   - Online        (navigator.onLine transitions from false → true)
 *
 * Retry strategy (exponential back-off):
 *   Attempt 0: immediate
 *   Attempt 1: 1 s
 *   Attempt 2: 2 s
 *   Attempt 3: 4 s
 *   Attempt 4: 8 s
 *   Attempt 5: 16 s → mark 'failed', show toast
 *
 * The queue survives page refreshes.  On mount, any 'pending' items from the
 * previous session are retried — but first compared with the server copy, and
 * marked 'stale' (not sent) if they would remove events saved there.  Items
 * waiting longer than API_SYNC_VERIFY_AFTER_MS get the same check.
 */

import axios from "axios";
import { syncQueueTable } from "../db/database";
import {
  API_SYNC_TYPING_PAUSE_MS,
  API_SYNC_PERIODIC_MS,
  API_SYNC_MAX_RETRIES,
  API_SYNC_RETRY_BASE_MS,
  API_SYNC_VERIFY_AFTER_MS,
  STRING_GUARD_MISSING_SHARE,
} from "../config";
import { SSENSE_URL } from "../../config/stepoutAPI";
import { markApiSynced } from "./SessionManager";
import { createCheckpoint } from "./CheckpointManager";
import { queueFirestoreWrite, flushFirestoreQueue } from "../firestore/firestoreSync";
import { countEvents, parseEventTokens } from "./DiffManager";
import { assessReplacement } from "../guard/stringGuard";

// ─── State ────────────────────────────────────────────────────────────────────

/** Is the sync loop currently paused (offline mode)? */
let isPaused = false;

/** Is the periodic sync interval running? */
let periodicTimer = null;

/** Typing-pause debounce timer — reset on every new string update. */
let typingPauseTimer = null;

/** Is a queue flush already in progress? Prevents concurrent flushes. */
let isFlushInProgress = false;

/** Identifies this page load; queue items written by earlier loads are re-checked. */
const PAGE_LOAD_ID = `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

/** External toast notification callback, injected by useSessionPersistence. */
let onSyncError = null;
let onSyncSuccess = null;
let onStaleSave = null;

// ─── Initialisation ───────────────────────────────────────────────────────────

/**
 * Starts the periodic sync timer and registers online/offline listeners.
 * Called once from useSessionPersistence on InputBoard mount.
 *
 * @param {object} callbacks
 * @param {function} callbacks.onSyncError    Called with an error message string
 * @param {function} callbacks.onSyncSuccess  Called when a sync succeeds (optional)
 * @param {function} callbacks.onStaleSave    Called with a message when a queued
 *                                            save is held back as stale (optional)
 */
export function startSyncManager({ onSyncError: errorCb, onSyncSuccess: successCb, onStaleSave: staleCb } = {}) {
  onSyncError   = errorCb   || null;
  onSyncSuccess = successCb || null;
  onStaleSave   = staleCb   || null;

  // Resume or pause based on current network state
  if (navigator.onLine === false) {
    isPaused = true;
  }

  // Network listeners
  window.addEventListener("online",  handleOnline);
  window.addEventListener("offline", handleOffline);

  // Periodic background sync
  _startPeriodicSync();

  // Retry any pending items from a previous session
  _flushQueue();
}

/**
 * Tears down timers and event listeners.
 * Called from useSessionPersistence cleanup on InputBoard unmount.
 */
export function stopSyncManager() {
  window.removeEventListener("online",  handleOnline);
  window.removeEventListener("offline", handleOffline);
  _stopPeriodicSync();
  if (typingPauseTimer) clearTimeout(typingPauseTimer);
}

// ─── Queue writes ─────────────────────────────────────────────────────────────

/**
 * Adds or updates the sync queue entry for a session.
 * We only keep one 'pending' entry per session — the latest string wins.
 * This means N rapid updates collapse into a single API call.
 *
 * Also resets the typing-pause debounce timer.
 *
 * @param {object} params
 * @param {string} params.matchId
 * @param {string} params.analystId
 * @param {string} params.string      Latest event string
 * @param {string} params.token       Auth token from Redux
 */
export async function scheduleApiSync({ matchId, analystId, string, token }) {
  // Upsert the queue entry — latest string always wins
  await _upsertQueueItem({ matchId, analystId, string, token });

  // Reset typing-pause timer — flush after analyst stops typing for N seconds
  if (typingPauseTimer) clearTimeout(typingPauseTimer);
  typingPauseTimer = setTimeout(() => {
    _flushQueue();
  }, API_SYNC_TYPING_PAUSE_MS);
}

/**
 * Immediately flushes the sync queue without waiting for the typing pause.
 * Called on visibilitychange (hidden), beforeunload, and online events.
 *
 * @returns {Promise<void>}
 */
export async function flushNow() {
  if (typingPauseTimer) clearTimeout(typingPauseTimer);
  await _flushQueue();
}

// ─── Online / Offline ─────────────────────────────────────────────────────────

function handleOnline() {
  isPaused = false;
  // Immediately retry any queued items now that we're back online
  _flushQueue();
}

function handleOffline() {
  isPaused = true;
}

export function isOnline() {
  return navigator.onLine !== false;
}

// ─── Private: queue management ────────────────────────────────────────────────

/**
 * Upserts a sync queue entry.
 * Finds any existing 'pending' item for the session and updates it.
 * If none exists, inserts a new one.
 */
async function _upsertQueueItem({ matchId, analystId, string, token }) {
  try {
    const existing = await syncQueueTable()
      .where("[matchId+analystId]")
      .equals([matchId, analystId])
      .filter((r) => r.status === "pending")
      .first();

    // NOTE: the auth token is intentionally NOT persisted here. Storing a
    // bearer credential in IndexedDB leaves it readable via DevTools and across
    // tab closes. The token is read from sessionStorage at flush time instead.
    if (existing) {
      await syncQueueTable().update(existing.id, {
        string,
        pageId: PAGE_LOAD_ID,
        queuedAt: Date.now(),
        attempts: 0,            // reset attempts — string is fresh
        nextAttemptAt: null,
      });
    } else {
      await syncQueueTable().add({
        matchId,
        analystId,
        string,
        pageId:       PAGE_LOAD_ID,
        queuedAt:     Date.now(),
        attempts:     0,
        nextAttemptAt: null,
        status:       "pending",
        lastError:    null,
      });
    }
  } catch (error) {
    console.warn("[SyncManager] _upsertQueueItem failed:", error.message);
  }
}

/**
 * Processes all 'pending' sync queue items that are due for retry.
 * Serialised — only one flush runs at a time.
 */
async function _flushQueue() {
  if (isPaused || isFlushInProgress) return;
  isFlushInProgress = true;

  try {
    const now = Date.now();
    const pending = await syncQueueTable()
      .where("status")
      .equals("pending")
      .filter((r) => !r.nextAttemptAt || r.nextAttemptAt <= now)
      .toArray();

    for (const item of pending) {
      await _processQueueItem(item);
    }
  } catch (error) {
    console.warn("[SyncManager] _flushQueue error:", error.message);
  } finally {
    isFlushInProgress = false;
  }
}

/**
 * Attempts to push a single queue item to the REST API.
 * On success: marks done, creates api-success checkpoint, queues Firestore backup.
 * On failure: schedules retry with exponential back-off.
 */
async function _processQueueItem(item) {
  // Read the token from sessionStorage (per-tab, cleared on tab close) rather
  // than from the persisted queue item — the token is never written to IDB.
  const token = item.token ?? sessionStorage.getItem("token");
  if (!token) {
    // No credential available (e.g. logged out / fresh tab) — leave pending.
    await syncQueueTable().update(item.id, { status: "pending" });
    return;
  }

  // Mark as processing to prevent concurrent attempts
  try {
    await syncQueueTable().update(item.id, { status: "processing" });
  } catch {
    return; // another process is handling this item
  }

  try {
    if (_needsServerCheck(item)) {
      const { verdict, reasons } = await _checkAgainstServer(item, token);
      if (verdict === "same") {
        await syncQueueTable().update(item.id, { status: "done" });
        return;
      }
      if (verdict === "stale") {
        // Kept in the table (not pruned) so the string can still be recovered.
        await syncQueueTable().update(item.id, { status: "stale", lastError: reasons.join(" ") });
        if (onStaleSave) {
          onStaleSave(
            `An older unsent copy of match ${item.matchId} on this device was not sent to the server, ` +
              `because it would remove events saved there. ${reasons.join(" ")}`
          );
        }
        return;
      }
    }

    const response = await axios.post(
      `${SSENSE_URL}push_match_video_string/v2/`,
      {
        match_id:     item.matchId,
        analyst_id:   item.analystId,
        match_string: item.string,
      },
      {
        headers: { Authorization: "ANALYST " + token },
        timeout: 15_000, // 15s timeout — generous for slow mobile connections
      }
    );

    if (response.status === 200) {
      // ✅ Success
      await syncQueueTable().update(item.id, { status: "done" });
      await markApiSynced(item.matchId, item.analystId);

      // Create api-success checkpoint.  Both IndexedDB and Firestore receive this,
      // but Firestore is rate-limited to FIRESTORE_API_SUCCESS_MIN_INTERVAL_MS
      // (20 min) inside CheckpointManager so rapid back-to-back API syncs collapse
      // into a single Firestore write rather than one write per 2-min sync cycle.
      await createCheckpoint({
        matchId:       item.matchId,
        analystId:     item.analystId,
        currentString: item.string,
        triggerType:   "api-success",
      });

      // Queue a Firestore backup of the live string.  The upsert logic in
      // queueFirestoreWrite ensures only one pending backup exists at a time —
      // multiple API successes within the same 30-min flush window collapse
      // into a single Firestore setDoc.  The periodic timer in startFirestoreSync
      // (FIRESTORE_BACKUP_PERIODIC_MS = 30 min) handles the flush; calling
      // flushFirestoreQueue() here would bypass that batching and cause one
      // Firestore write per API sync (every 2 min = 30 writes/hr/analyst).
      await queueFirestoreWrite({
        matchId:   item.matchId,
        analystId: item.analystId,
        type:      "session-backup",
        payload: {
          currentString: item.string,
          eventCount:    countEvents(item.string),
          updatedAt:     Date.now(),
        },
      });

      if (onSyncSuccess) onSyncSuccess();
    } else {
      throw new Error(`HTTP ${response.status}`);
    }
  } catch (error) {
    await _handleSyncFailure(item, error);
  }
}

/**
 * A save written by an earlier page load (e.g. left unsent when a phone went
 * offline or the tab was killed) or waiting a long time may be older than what
 * another device has since saved, so it is compared with the server first.
 */
function _needsServerCheck(item) {
  return item.pageId !== PAGE_LOAD_ID || Date.now() - (item.queuedAt ?? 0) > API_SYNC_VERIFY_AFTER_MS;
}

/**
 * Compares a queued save with the server copy.
 *   "same"  — the server already has this string
 *   "stale" — sending it would remove events the server has
 *   "send"  — safe to send
 * Throws when the server can't be reached, so the item is retried later.
 *
 * @returns {Promise<{verdict: "same"|"stale"|"send", reasons: string[]}>}
 */
async function _checkAgainstServer(item, token) {
  const { data } = await axios.post(
    `${SSENSE_URL}get_match_video_string/v2/`,
    { match_id: item.matchId, analyst_id: item.analystId },
    { headers: { Authorization: "ANALYST " + token }, timeout: 15_000 }
  );
  const serverString = data?.temp_string ?? "";
  if (!serverString) return { verdict: "send", reasons: [] };
  if (parseEventTokens(serverString).join(",") === parseEventTokens(item.string).join(",")) {
    return { verdict: "same", reasons: [] };
  }
  // Only removals count: a queued save with extra events (e.g. tagged offline)
  // is newer work and must still be sent.
  const { comparable, missingShare, prevCount, nextCount } = assessReplacement(serverString, item.string);
  if (comparable && missingShare >= STRING_GUARD_MISSING_SHARE) {
    return {
      verdict: "stale",
      reasons: [
        `${Math.round(missingShare * 100)}% of the server's events are missing from it ` +
          `(server: ${prevCount} events, this copy: ${nextCount}).`,
      ],
    };
  }
  return { verdict: "send", reasons: [] };
}

/**
 * Handles a failed sync attempt.
 * Schedules a retry with exponential back-off or marks as 'failed' after
 * MAX_RETRIES attempts.
 */
async function _handleSyncFailure(item, error) {
  const attempts = (item.attempts || 0) + 1;
  const errorMessage = error?.message || "Unknown error";

  if (attempts >= API_SYNC_MAX_RETRIES) {
    // Permanently failed — surface to user
    await syncQueueTable().update(item.id, {
      status:    "failed",
      attempts,
      lastError: errorMessage,
    });

    if (onSyncError) {
      onSyncError(
        "Could not sync to server after 5 attempts. Your data is safe locally — try saving manually."
      );
    }
  } else {
    // Schedule retry with exponential back-off
    const delayMs     = API_SYNC_RETRY_BASE_MS * Math.pow(2, attempts);
    const nextAttemptAt = Date.now() + delayMs;

    await syncQueueTable().update(item.id, {
      status:       "pending",  // back to pending so _flushQueue picks it up
      attempts,
      nextAttemptAt,
      lastError:    errorMessage,
    });
  }
}

// ─── Periodic sync ────────────────────────────────────────────────────────────

function _startPeriodicSync() {
  if (periodicTimer) return;
  periodicTimer = setInterval(() => {
    _flushQueue();
  }, API_SYNC_PERIODIC_MS);
}

function _stopPeriodicSync() {
  if (periodicTimer) {
    clearInterval(periodicTimer);
    periodicTimer = null;
  }
}

// ─── Queue cleanup ────────────────────────────────────────────────────────────

/**
 * Removes old 'done' sync queue entries.
 * Called on session start to prevent unbounded table growth.
 *
 * @param {number} maxAgeMs  Entries older than this are deleted. Default: 24 hours.
 */
export async function pruneCompletedQueueItems(maxAgeMs = 24 * 60 * 60_000) {
  try {
    const cutoff = Date.now() - maxAgeMs;
    await syncQueueTable()
      .where("status")
      .equals("done")
      .filter((r) => r.queuedAt < cutoff)
      .delete();
  } catch (error) {
    console.warn("[SyncManager] pruneCompletedQueueItems failed:", error.message);
  }
}

/**
 * Returns the count of pending and failed sync items.
 * Used by useSyncStatus hook for UI indicators.
 *
 * @returns {Promise<{ pending: number, failed: number }>}
 */
export async function getSyncQueueStatus() {
  try {
    const [pending, failed] = await Promise.all([
      syncQueueTable().where("status").equals("pending").count(),
      syncQueueTable().where("status").equals("failed").count(),
    ]);
    return { pending, failed };
  } catch {
    return { pending: 0, failed: 0 };
  }
}
